import React, { useCallback, useMemo, useState } from "react";
import { defineMessages } from "react-intl";
import {
    HierarchyPath,
    TreeCheckboxGroup,
    TreeNode,
} from "../../../../../../components/TreeCheckboxGroup";
import { LegacyLoader } from "../../../../../../components/Loader/Loader";
import {
    getInitialActiveNodeFromURL,
    getInitialExpandedNodesFromURL,
} from "../utils";
import { CreateURLParams } from "../../const";
import { useParams } from "react-router-dom";
import { PlaylistAction } from "../playlistReducer";
import useResourceURL from "../useResourceURL";
import { ResourcePath } from "../../utils";

import "./TeacherPlaylistSelect.scss";

type Props = {
    checkedResources: ResourcePath[];
    resources?: Record<string, TreeNode>;
    entryIds: string[];
    dispatch: React.Dispatch<PlaylistAction>;
};

const TeacherPlaylistSelect = ({
    dispatch,
    checkedResources,
    resources,
    entryIds,
}: Props) => {
    const urlParams = useParams<CreateURLParams>();

    const [activeResource, setActiveResource] = useState<
        HierarchyPath | undefined
    >(getInitialActiveNodeFromURL(urlParams));

    const { goToResourceURL } = useResourceURL();
    const activateResourceHandler = useCallback(
        (nodePath: HierarchyPath) => {
            setActiveResource(nodePath);
            goToResourceURL(nodePath);
        },
        [goToResourceURL]
    );

    const [expandedResources, setExpandedResources] = useState<HierarchyPath[]>(
        getInitialExpandedNodesFromURL(urlParams)
    );
    const expandResourcesHandler = useCallback((path: HierarchyPath) => {
        setExpandedResources((curr) => {
            if (curr.includes(path))
                return curr.filter((node) => node !== path);
            return [...curr, path];
        });
    }, []);

    const checkNode = useCallback(
        (nodePath: HierarchyPath) => {
            if (!resources) return;
            dispatch({
                type: "check_learning_item",
                learning_item: nodePath,
                resources,
            });
        },
        [resources, dispatch]
    );

    const _checkedResources = useMemo(
        () => checkedResources.map((resource) => resource.toEditorPath()),
        [checkedResources]
    );

    if (!resources) return <LegacyLoader />;

    return (
        <div className="playlist__select">
            <TreeCheckboxGroup
                rootChildrenIds={entryIds}
                selectBranchLabel={bundleStateToMessage}
                selectLeafLabel={resourceStateToMessage}
                nodes={resources}
                checkedLeaves={_checkedResources}
                onCheckNode={checkNode}
                activeNode={activeResource}
                onActivateNode={activateResourceHandler}
                expandedNodes={expandedResources}
                onExpandNode={expandResourcesHandler}
                accentColor="var(--element-teacher-default)"
            />
        </div>
    );
};
export default TeacherPlaylistSelect;

const messages = defineMessages({
    addBundleToPlaylist: {
        id: "playlist-addBundleToPlaylist",
        defaultMessage: "Add resources in {branch} to the playlist",
    },
    removeBundleFromPlaylist: {
        id: "playlist-removeBundleFromPlaylist",
        defaultMessage: "Remove resources in {branch} from the playlist",
    },
    addResourceToPlaylist: {
        id: "playlist-addResourceToPlaylist",
        defaultMessage: "Add {label} to the playlist",
    },
    removeResourceFromPlaylist: {
        id: "playlist-removeResourceFromPlaylist",
        defaultMessage: "Remove {label} from the playlist",
    },
});
const bundleStateToMessage = (
    state: boolean | "mixed"
): (typeof messages)[keyof typeof messages] => {
    switch (state) {
        case true:
        case "mixed":
            return messages.removeBundleFromPlaylist;
        case false:
            return messages.addBundleToPlaylist;
        default: {
            const _: never = state;
            throw new Error("Case not handled");
        }
    }
};
const resourceStateToMessage = (state: boolean) => {
    switch (state) {
        case true:
            return messages.removeResourceFromPlaylist;
        case false:
            return messages.addResourceToPlaylist;
        default: {
            const _: never = state;
            throw new Error("Case not handled");
        }
    }
};
